import { createContext, useState, ReactElement, useMemo, useContext, useCallback } from 'react';

import { PermissionsInterface } from '../../types/AuthenticationInterface';
import { LOCAL_STORAGE_PERMISSIONS } from '../../constants/AppConstants';

interface PermissionsContextInterface {
  permissions: PermissionsInterface;
  setUserPermissions: Function;
  clearUserPermissions: Function;
}

interface PermissionsProviderInterface {
  children: ReactElement;
}

const INITIAL_PERMISSIONS = {
  // TODO: Clean Up pairings permissions. Leaving for now in case we are refactoring how permissions are handled overall
  hasPairings: false,
  hasImageUpload: false
};

// `hasPairings`/`hasImageUpload` only ever arrived once, from the /login response -- with no
// persistence, any full page reload silently reset them to INITIAL_PERMISSIONS (both false),
// hiding every isSuper-gated control app-wide with no error, even for a genuinely-super account
// that never logged out. Persisting to localStorage (not sensitive -- just two booleans, same
// tier of "annoying to lose" as the auth cookie) makes a reload survive the way staying logged
// in already does.
const readStoredPermissions = (): PermissionsInterface => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_PERMISSIONS);
    if (!raw) {
      return INITIAL_PERMISSIONS;
    }
    const parsed = JSON.parse(raw);
    return {
      hasPairings: Boolean(parsed.hasPairings),
      hasImageUpload: Boolean(parsed.hasImageUpload)
    };
  } catch {
    return INITIAL_PERMISSIONS;
  }
};

const PermissionsContext = createContext<PermissionsContextInterface>({
  permissions: INITIAL_PERMISSIONS,
  setUserPermissions: () => {},
  clearUserPermissions: () => {}
});

const PermissionsProvider = ({ children }: PermissionsProviderInterface) => {
  const [permissions, setPermissions] = useState<PermissionsInterface>(readStoredPermissions);

  const setUserPermissions = useCallback(
    (userPermissions: PermissionsInterface) => {
      const merged = { ...permissions, ...userPermissions };
      setPermissions(merged);
      localStorage.setItem(LOCAL_STORAGE_PERMISSIONS, JSON.stringify(merged));
    },
    [permissions]
  );

  const clearUserPermissions = useCallback(() => {
    setPermissions(INITIAL_PERMISSIONS);
    localStorage.removeItem(LOCAL_STORAGE_PERMISSIONS);
  }, []);

  const providerValue = useMemo(
    () => ({ permissions, setUserPermissions, clearUserPermissions }),
    [permissions, setUserPermissions, clearUserPermissions]
  );
  return <PermissionsContext.Provider value={providerValue}>{children}</PermissionsContext.Provider>;
};

const usePermissionsContext = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error(`usePermissionsContext must be used within the PermissionsProvider component`);
  }
  return context;
};

export { PermissionsProvider as default, usePermissionsContext };
